import React from "react";

/**
 * mediaIcon.js
 * A set of SVG icon components for social/media stat bars, in display order:
 * likes, comments, views, support, shares, music/sound.
 *
 * Usage:
 *   import { HeartIcon, CommentIcon, EyeIcon, SupportIcon, ShareIcon, MusicIcon } from "./mediaIcon";
 *
 *   <CommentIcon size={24} color="#fff" />
 */

const defaultProps = {
  size: 24,
  color: "currentColor",
};

/** Heart — likes */
export function HeartIcon({ size = defaultProps.size, color = defaultProps.color, ...rest }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path
        d="M12 21s-6.716-4.35-9.428-8.06C.86 10.44 1.02 7.2 3.6 5.4c2.26-1.58 5.02-1.02 6.4 1 .38.55.7 1.1 1 1.6.3-.5.62-1.05 1-1.6 1.38-2.02 4.14-2.58 6.4-1 2.58 1.8 2.74 5.04 1.03 7.54C18.72 16.65 12 21 12 21z"
        fill={color}
      />
    </svg>
  );
}

/** Round speech bubble with three dots — comments (TikTok style).
 *  Circular body with a small tail at the bottom-right. The dots are real
 *  cut-outs (evenodd), so the background shows through. */
export function CommentIcon({ size = defaultProps.size, color = defaultProps.color, ...rest }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        fill={color}
        d="M14.90 20.14A9.4 9.4 0 1 1 18.76 17.73L20.00 21.20ZM6.4 11.2a1.4 1.4 0 1 0 2.8 0 1.4 1.4 0 1 0-2.8 0zM10.6 11.2a1.4 1.4 0 1 0 2.8 0 1.4 1.4 0 1 0-2.8 0zM14.8 11.2a1.4 1.4 0 1 0 2.8 0 1.4 1.4 0 1 0-2.8 0z"
      />
    </svg>
  );
}

/** Eye — views */
export function EyeIcon({ size = defaultProps.size, color = defaultProps.color, ...rest }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path
        d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"
        fill={color}
      />
      <circle cx="12" cy="12" r="3" fill="black" fillOpacity="0.35" />
    </svg>
  );
}

/** Gift box with a bow — support / tip */
export function SupportIcon({ size = defaultProps.size, color = defaultProps.color, ...rest }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      {/* lid (left / right of the ribbon) */}
      <path
        d="M3 8.5A1.5 1.5 0 0 1 4.5 7H11v4H4.5A1.5 1.5 0 0 1 3 9.5v-1z"
        fill={color}
      />
      <path
        d="M13 7h6.5A1.5 1.5 0 0 1 21 8.5v1a1.5 1.5 0 0 1-1.5 1.5H13V7z"
        fill={color}
      />
      {/* box (left / right of the ribbon) */}
      <path
        d="M4.5 12.5H11V21H6.5a2 2 0 0 1-2-2v-6.5z"
        fill={color}
      />
      <path
        d="M13 12.5h6.5V19a2 2 0 0 1-2 2H13v-8.5z"
        fill={color}
      />
      {/* bow */}
      <path
        d="M12 7C11 4.2 9.6 3 8.3 3.6 7 4.2 7.3 6.2 9.4 7M12 7c1-2.8 2.4-4 3.7-3.4 1.3.6 1 2.6-1.1 3.4"
        stroke={color}
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** @deprecated renamed to SupportIcon — kept so old imports don't break. */
export const CareIcon = SupportIcon;

/** Share — TikTok-style forward arrow */
export function ShareIcon({ size = defaultProps.size, color = defaultProps.color, ...rest }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path
        d="M14 4.5 21.5 11.5 14 18.5V14.3C9.2 14.3 5.8 15.7 3.5 19.5 4 13.2 7.6 9.2 14 8.6Z"
        fill={color}
        stroke={color}
        strokeWidth="1"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Music note — sound / audio */
export function MusicIcon({ size = defaultProps.size, color = defaultProps.color, ...rest }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path
        d="M9 18V6.5l10-2v11.5"
        stroke={color}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="7" cy="18" r="2.2" fill={color} />
      <circle cx="17" cy="16" r="2.2" fill={color} />
    </svg>
  );
}

/**
 * MediaIcon — a single stat item combining an icon with its count/label,
 * matching the vertical icon + number layout in the reference image.
 *
 * type: "heart" | "comment" | "eye" | "support" | "share" | "music"
 * ("care" still works as an alias of "support")
 */
const ICONS = {
  heart: HeartIcon,
  comment: CommentIcon,
  eye: EyeIcon,
  support: SupportIcon,
  care: SupportIcon, // legacy alias
  share: ShareIcon,
  music: MusicIcon,
};

export function MediaIcon({
  type,
  label,
  size = 22,
  color = "#fff",
  textColor = "#fff",
  gap = 4,
  style,
  ...rest
}) {
  const Icon = ICONS[type];
  if (!Icon) return null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap,
        fontFamily: "sans-serif",
        ...style,
      }}
      {...rest}
    >
      <Icon size={size} color={color} />
      {label != null && (
        <span style={{ color: textColor, fontSize: 12, fontWeight: 500 }}>
          {label}
        </span>
      )}
    </div>
  );
}

export default MediaIcon;